<script setup lang="ts">
import { sizes, states, stylesInput as styles, type IPropSizes, type IPropStyles, type IPropStates } from './styles';

export interface IOptionItem {
  value?: String | string | Number | number | null | undefined;
  name?: String | string | Number | number | undefined;
  text?: String | string | Number | number | undefined;
  disabled?: boolean | undefined;
};

interface Props extends IPropStyles, IPropSizes, IPropStates {
  vId?: String | string | undefined;
  title?: String | string | undefined;
  type?: 'select' | 'select-adv' | 'textarea' | 'checkbox' | 'text' | 'email' | 'url' | 'tel' | 'number' | 'range' | 'radio' | 'color' | 'date' | 'month' | 'week' | 'time' | 'datetime-local' | 'password' | 'submit' | 'reset';
  accept?: String | string | undefined;
  value?: any | null | undefined;
  name?: String | string | undefined;
  stringify?: boolean | undefined;
  errors?: Object | Array<String | string> | undefined;
  block?: boolean | undefined;
  min?: Number | number | String | string | Date | undefined;
  max?: Number | number | String | string | Date | undefined;
  step?: Number | number | undefined;
  minlength?: Number | number | undefined;
  maxlength?: Number | number | undefined;
  trueValue?: String | string | undefined;
  falseValue?: String | string | undefined;
  placeholder?: String | string | undefined;
  multiple?: boolean | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  hidden?: boolean | undefined;
  options?: Array<IOptionItem> | undefined;
  optionNullSkip?: boolean | undefined;
  optionNullText?: String | string | undefined;
  plain?: boolean | undefined;
  className?: String | string | undefined;
  suggested?: String | string | undefined;
  autocomplete?: String | string | undefined;
  content?: '✔' | 'X' | String | string | undefined;
  rounded?: boolean | undefined;
  noGrid?: boolean | undefined;
  noBorder?: boolean | undefined;
  passwordVisibility?: boolean | undefined;
  nullDisabled?: boolean | undefined;
  overflow?: boolean | undefined;
  [x: string]: any;
};

const props = withDefaults(defineProps<Props>(), {
  trueValue: '1',
  falseValue: '0',
  step: 1,
  styleName: 'default',
  rounded: true,
  state: null,
  disabled: false,
  stringify: true,
  passwordVisibility: true,
  nullDisabled: true,
  overflow: true,
  content: '✔'
});

const boxSizes = {
  none: 'min-w-7',
  sm: 'min-w-9',
  md: 'min-w-10',
  lg: 'min-w-11',
  xl: 'min-w-12',
  '2xl': 'min-w-12',
};

const slots = useSlots();

const styleSize = computed<String | string>((): String | string => ((props.size !== undefined) ? props.size : useNuxtApp()?.$styleSize?.value));

const formatData = (value: any): any => {
  if ((value === null) || (value === undefined))
    return value;
  const dateTypes = ['date', 'month', 'week', 'time', 'datetime-local'];
  if (dateTypes.includes(props.type))
    return Date.toInputString(value, props.type);
  else if ((props.type === 'checkbox') && (typeof value === 'boolean'))
    return ((value === true) ? props.trueValue : props.falseValue);
  return value;
}

const model = defineModel<any>({
  required: false,
  default: null,
  get(rawValue: any) {
    return formatData(rawValue);
  },
});

const refInput = ref<Object>(null);

const init = (): void => {
  if (props.value !== undefined)
    model.value = props.value;
  if ((model.value === null) || (model.value === undefined)) {
    if ((props.type == 'checkbox') && (model.value === undefined) && (! props.multiple)) // DO NOT INCLUDE NULL CASE -> NULL IS NULL and it is core for functionality!
      model.value = props.falseValue;
    if (((props.type == 'select') || (props.type == 'select-adv') || (props.type == 'checkbox')) && props.multiple && (! Array.isArray(model.value)))
      model.value = [];
  }
};
init();

const emit = defineEmits(['click', 'change', 'input', 'blur', 'focus']);

const contentComp = computed<String | string>((): String | string => {
  // return `before:content-["${props.content}"]`; Makes it into broken component
  let result = 'before:content-["✔"]'
  if (props.content)
    result = result.replace('✔', props.content);
  return result;
});

const optionsComp = computed<Array<IOptionItem>>((): Array<IOptionItem> => {
  if ((! props.optionNullSkip) && (! props?.options?.find((option: IOptionItem) => (option.value === null))))
    return [{
      value: null,
      text: (props.optionNullText ?? (props.required ? ('Zvoľte možnosť' + (props.multiple ? 'i!' : '!')) : 'Ak je možné, vyberte.')),
      disabled: (props?.required ?? false)
    }].union(props.options);
  return props.options;
});

const pwdVisible = ref<boolean>(false);
const collapse = ref<boolean>(false);

const internalId = (props.vId ?? useId());

// Expose usefull functions
defineExpose({ init, refInput });
</script>

<template>
  <div
    v-if="((! hidden) || errors)"
    :class="[
      ((! noGrid) && ('grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 xl:gap-2 items-center'))
    ]">
    <slot
      v-if="(! plain)"
      name="title-adv">
      <legend
        v-if="((title || slots['title']) && (type == 'checkbox') && (! hidden) && multiple)"
        class="col-span-1 font-medium w-full">
        <BaseText
          block
          :size="size"
          :disabled="disabled"
          :style-name="styleName">
          <slot name="title">
            {{ title }}
          </slot>
          <b v-show="required" class="px-2 text-red-500">*</b>
        </BaseText>
      </legend>
      <label
        v-else-if="((title || slots['title']) && (type != 'hidden') && (! hidden))"
        :for="internalId"
        class="col-span-1 font-medium w-full">
        <BaseText
          block
          :size="size"
          :disabled="disabled"
          :style-name="styleName">
          <slot name="title">
            {{ title }}
          </slot>
          <b v-show="required" class="px-2 text-red-500">*</b>
        </BaseText>
      </label>
      <label
        v-else-if="((! title) && (type != 'hidden') && (! hidden))"
        :for="internalId"
        class="sr-only"
        v-text="type" />
    </slot>
    <div
      :class="[
        (((! plain) && (title || slots['title-adv'] || slots['title']) && (type != 'hidden') && (! hidden)) && ('col-start-2')),
        (((! plain) && (type != 'hidden') && (! hidden)) && ('col-span-full flex flex-col justify-center gap-1 xl:gap-2')),
        ('h-full'),
        (block ? 'w-full' : 'w-fit')
      ]">
      <div
        v-if="(((type == 'select') || (type == 'select-adv')) && (! hidden))"
        :class="[
          ('flex items-center w-full h-full'),
          (((type != 'select-adv') && (! multiple)) && 'relative'),
          states[state],
          ((! disabled) && styles[styleName]),
          (disabled && styles['muted']),
          (rounded && 'rounded-auto')
        ]"
        :disabled="(disabled ? true : null)">
        <div
          v-if="(type == 'select-adv')"
          :class="[
            (rounded && 'rounded-auto'),
            ('h-full appearance-none text-auto'),
            ((! noBorder) && 'border-2 outline-none'),
            states[state],
            (block ? 'w-full' : 'w-fit'),
            ((! disabled) && styles[styleName]),
            (disabled && styles['muted']),
            ((size !== undefined) ? sizes[size] : 'padding-auto'),
            className
          ]"
          :disabled="(disabled ? true : null)">
          <div
            :class="[
              ('flex flex-row gap-1 xl:gap-2'),
              (collapse && ((size !== undefined) ? sizes[size] : 'padding-auto')),
              (collapse && 'pt-0 !px-0')
            ]">
            <ul
              :class="[
                ('flex flex-col gap-1 xl:gap-2 w-full h-full overflow-auto scroll-smooth'),
                (overflow && 'max-h-[20vh]')
              ]">
              <li
                v-if="(multiple && Array.isArray(model) && (model.length > 0))"
                v-for="(value, key) in model" :key="key"
                class="w-full h-full">
                <slot
                  name="option-adv"
                  :option="optionsComp.find((option: IOptionItem) => (value === ((option.value !== undefined) ? option.value : option)))"
                  :options="optionsComp.filter((option: IOptionItem) => (Array.isArray(model) ? model.includes(((option.value !== undefined) ? option.value : option)) : (model === ((option.value !== undefined) ? option.value : option))))">
                  <BaseBox
                    class="h-full cursor-pointer disabled:cursor-not-allowed disabled:bg-slate-100 hover:bg-slate-100 bg-slate-200"
                    :rounded="rounded"
                    :size="size"
                    :disabled="((disabled || (required && (model.length === 1))) ? true : null)"
                    block
                    @click="() => ((! (disabled || (required && (model.length === 1)))) ? model.splice(key, 1) : null)">
                    <slot
                      name="option"
                      :option="optionsComp.find((option: IOptionItem) => (value === ((option.value !== undefined) ? option.value : option)))"
                      :options="optionsComp.filter((option: IOptionItem) => (Array.isArray(model) ? model.includes(((option.value !== undefined) ? option.value : option)) : (model === ((option.value !== undefined) ? option.value : option))))">
                      {{ (optionsComp.find((option: IOptionItem) => (value === ((option.value !== undefined) ? option.value : option)))?.text ?? ((optionsComp.find((option: IOptionItem) => (value === ((option.value !== undefined) ? option.value : option)))?.value !== undefined) ? optionsComp.find((option: IOptionItem) => (value === ((option.value !== undefined) ? option.value : option)))?.value : optionsComp.find((option: IOptionItem) => ((option.value !== undefined) ? option.value : option) === model))) }}
                    </slot>
                  </BaseBox>
                </slot>
              </li>
              <li v-else-if="(multiple && Array.isArray(model))">
                <slot
                  name="option-adv"
                  :option="optionsComp.find((option: IOptionItem) => (option.value === null))"
                  :options="[optionsComp.find((option: IOptionItem) => (option.value === null))]">
                  <BaseBox
                    class="h-full cursor-pointer disabled:cursor-not-allowed disabled:bg-slate-100 hover:bg-slate-100 bg-slate-200"
                    :rounded="rounded"
                    :size="size"
                    :disabled="(((disabled || required) || props.nullDisabled) ? true : null)"
                    block>
                    <slot
                      name="option"
                      :option="optionsComp.find((option: IOptionItem) => (option.value === null))"
                      :options="[optionsComp.find((option: IOptionItem) => (option.value === null))]">
                      {{ (optionsComp.find((option: IOptionItem) => (option.value === null))?.text ?? ((optionsComp.find((option: IOptionItem) => (option.value === null))?.value !== undefined) ? optionsComp.find((option: IOptionItem) => (option.value === null))?.value : optionsComp.find((option: IOptionItem) => (option.value === null)))) }}
                    </slot>
                  </BaseBox>
                </slot>
              </li>
              <li v-else>
                <slot
                  name="option-adv"
                  :option="optionsComp.find((option: IOptionItem) => (model === ((option.value !== undefined) ? option.value : option)))"
                  :options="[optionsComp.find((option: IOptionItem) => (model === ((option.value !== undefined) ? option.value : option)))]">
                  <BaseBox
                    class="cursor-pointer disabled:cursor-not-allowed disabled:bg-slate-100 hover:bg-slate-100 bg-slate-200"
                    :rounded="rounded"
                    :size="size"
                    :disabled="(((disabled || required) || ((optionsComp.find((option: IOptionItem) => (model === ((option.value !== undefined) ? option.value : option)))?.value === null) && props.nullDisabled) || optionsComp.find((option: IOptionItem) => (model === ((option.value !== undefined) ? option.value : option)))?.disabled) ? true : null)"
                    block
                    @click="() => ((! (disabled || required)) ? (model = null) : null)">
                    <slot
                      name="option"
                      :option="optionsComp.find((option: IOptionItem) => (model === ((option.value !== undefined) ? option.value : option)))"
                      :options="[optionsComp.find((option: IOptionItem) => (model === ((option.value !== undefined) ? option.value : option)))]">
                      {{ (optionsComp.find((option: IOptionItem) => (model === ((option.value !== undefined) ? option.value : option)))?.text ?? ((optionsComp.find((option: IOptionItem) => (model === ((option.value !== undefined) ? option.value : option)))?.value !== undefined) ? optionsComp.find((option: IOptionItem) => (model === ((option.value !== undefined) ? option.value : option)))?.value : optionsComp.find((option: IOptionItem) => (model === ((option.value !== undefined) ? option.value : option))))) }}
                    </slot>
                  </BaseBox>
                </slot>
              </li>
            </ul>
            <BaseButton
              :size="size"
              :rounded="rounded"
              :disabled="disabled"
              style-name="outline"
              @click="(e: Event) => (collapse = (! collapse))">
              <BaseText
                class="flex items-center justify-center"
                :size="size"
                block
                :disabled="disabled"
                :style-name="styleName">
                <Icon
                  :class="[
                    'transition-all transform',
                    (collapse && 'rotate-180')
                  ]"
                  name="bx:down-arrow"
                  size="18"
                  :title="(collapse ? 'Skryť' :'Zobraziť')" />
              </BaseText>
            </BaseButton>
          </div>
          <BaseCollapse
            v-model="collapse"
            class="open:animate-[down-fade-in_200ms_ease-in]">
            <div
              :class="[
                ('border-t-2'),
                ((size !== undefined) ? sizes[size] : 'padding-auto'),
                ('pb-0 !px-0')
              ]">
              <ul
                :class="[
                  ('flex flex-col gap-1 xl:gap-2 w-full h-full overflow-auto scroll-smooth'),
                  (overflow && 'max-h-[30vh]')
                ]">
                <li
                  v-for="option in optionsComp.filter((option: IOptionItem) => ((option?.value !== null) && ((Array.isArray(model) && multiple) ? (! model.includes(((option.value !== undefined) ? option.value : option))) : (model !== ((option.value !== undefined) ? option.value : option)))))"
                  :key="((option.value !== undefined) ? option.value : option)">
                  <slot
                    name="option-adv"
                    :option="option"
                    :options="optionsComp">
                    <BaseBox
                      class="h-full cursor-pointer disabled:cursor-not-allowed disabled:bg-slate-100 hover:bg-slate-100"
                      :rounded="rounded"
                      :size="size"
                      :disabled="((disabled || option.disabled) ? true : null)"
                      block
                      @click="() => ((! (disabled || option.disabled)) ? (multiple ? (Array.isArray(model) ? model.push(((option.value !== undefined) ? option.value : option)) : (model = [((option.value !== undefined) ? option.value : option)])) : (model = ((option.value !== undefined) ? option.value : option))) : null)">
                      <slot
                        name="option"
                        :option="option"
                        :options="optionsComp">
                        {{ (option.text ?? ((option.value !== undefined) ? option.value : option)) }}
                      </slot>
                    </BaseBox>
                  </slot>
                </li>
              </ul>
            </div>
          </BaseCollapse>
        </div>
        <select
          v-else
          :ref="(el) => { refInput = el }"
          :class="[
            ('h-full pr-8 lg:pr-9 xl:pr-10 text-auto'),
            (rounded && 'rounded-auto'),
            ((! noBorder) && 'border-2 outline-none'),
            states[state],
            (block ? 'w-full' : 'w-fit'),
            ((! disabled) && styles[styleName]),
            (disabled && styles['muted']),
            ((size !== undefined) ? sizes[size] : 'padding-auto'),
            className
          ]"
          :id="internalId"
          :name="((name && (! stringify)) ? name : null)"
          :multiple="multiple"
          :placeholder="placeholder"
          :required="required"
          :disabled="disabled"
          v-model="model"
          :suggested="suggested"
          :autocomplete="autocomplete"
          @click="(e: Event) => ((! disabled) ? emit('click', e) : null)"
          @change="(e: Event) => ((! disabled) ? emit('change', e) : null)"
          @blur="(e: Event) => ((! disabled) ? (collapse = false, emit('blur', e)) : null)"
          @input="(e: Event) => ((! disabled) ? emit('input', e) : null)"
          @focus="(e: Event) => ((! disabled) ? (collapse = true, emit('focus', e)) : null)"
          @mousedown="(e: Event) => ((! disabled) ? (collapse = (! collapse)) : null)">
          <slot
            name="options"
            :options="optionsComp">
            <option
              v-for="option in optionsComp" :key="((option.value !== undefined) ? option.value : option)"
              :value="((option.value !== undefined) ? option.value : option)"
              :disabled="option.disabled">
              <slot
                name="option"
                :option="option"
                :options="optionsComp">
                {{ (option.text ?? ((option.value !== undefined) ? option.value : option)) }}
              </slot>
            </option>
          </slot>
        </select>
        <Icon
          v-if="((type != 'select-adv') && (! multiple))"
          :class="[
            ('absolute flex items-center inset-y-0 right-0 mr-3 h-full cursor-pointer focus:outline-none pointer-events-none transition-all transform'),
            (collapse && 'rotate-180')
          ]"
          name="bx:down-arrow"
          size="18"
          :title="(collapse ? 'Skryť' :'Zobraziť')" />
      </div>
      <textarea
        v-else-if="((type == 'textarea') && (! hidden))"
        :ref="(el) => { refInput = el }"
        :class="[
          ('h-full text-auto'),
          (rounded && 'rounded-auto'),
          ((! noBorder) && 'border-2 outline-none'),
          (block ? 'w-full' : 'w-fit'),
          ((size !== undefined) ? sizes[size] : 'padding-auto'),
          ((! disabled) && styles[styleName]),
          (disabled && styles['muted']),
          states[state],
          className
        ]"
        :id="internalId"
        :name="((name && (! stringify)) ? name : null)"
        :minlength="minlength"
        :maxlength="maxlength"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        v-model="model"
        :suggested="suggested"
        :autocomplete="autocomplete"
        @click="(e: Event) => ((! disabled) ? emit('click', e) : null)"
        @change="(e: Event) => ((! disabled) ? emit('change', e) : null)"
        @blur="(e: Event) => ((! disabled) ? emit('blur', e) : null)"
        @input="(e: Event) => ((! disabled) ? emit('input', e) : null)"
        @focus="(e: Event) => ((! disabled) ? emit('focus', e) : null)">
      </textarea>
      <div
        v-else-if="((type == 'checkbox') && (! hidden) && multiple)"
        v-for="option in options" :key="((option.value !== undefined) ? option.value : option)"
        class="flex flex-row gap-1 xl:gap-2 items-center">
        <input
          :ref="(el) => { refInput = el }"
          :class="[
            ('h-fit w-fit text-auto align-middle text-center'),
            boxSizes[styleSize],
            (rounded && 'rounded-auto'),
            ((! noBorder) && 'border-2 outline-none'),
            ((size !== undefined) ? sizes[size] : 'padding-auto'),
            ((! disabled) && styles[styleName]),
            (disabled && styles['muted']),
            ('font-serif content-none outline-none before:text-transparent checked:before:text-current'),
            contentComp,
            states[state],
            className
          ]"
          :id="(internalId + ':' + option.value)"
          type="checkbox"
          :checked="model?.includesAdv(option.value)"
          :true-value="option.value"
          :false-value="null"
          :placeholder="placeholder"
          :required="(required && (!option.disabled))"
          :disabled="(option.disabled || disabled)"
          :suggested="suggested"
          :autocomplete="autocomplete"
          @click="(e: Event) => ((! (option.disabled || disabled)) ? emit('click', e) : null)"
          @change="(e: Event) => ((! (option.disabled || disabled)) ? ((model = (model?.includes(option.value) ? model?.diff([option.value]) : model?.union([option.value]))), emit('change', e)) : null)"
          @blur="(e: Event) => ((! (option.disabled || disabled)) ? emit('blur', e) : null)"
          @input="(e: Event) => ((! (option.disabled || disabled)) ? emit('input', e) : null)"
          @focus="(e: Event) => ((! (option.disabled || disabled)) ? emit('focus', e) : null)" />
        <label :for="(internalId + ':' + option.value)">
          <BaseText
            :block="block"
            :size="size"
            :disabled="(option.disabled || disabled)"
            :style-name="styleName" >
            {{ (option.text ?? ((option.value !== undefined) ? option.value : option)) }}
          </BaseText>
        </label>
      </div>
      <input
        v-else-if="((type == 'checkbox') && (! hidden))"
        :ref="(el) => { refInput = el }"
        :class="[
          ('h-fit w-fit text-auto align-middle text-center'),
          boxSizes[styleSize],
          (rounded && 'rounded-auto'),
          ((! noBorder) && 'border-2 outline-none'),
          ((size !== undefined) ? sizes[size] : 'padding-auto'),
          ((! disabled) && styles[styleName]),
          (disabled && styles['muted']),
          ('font-serif content-none outline-none before:text-transparent checked:before:text-current'),
          contentComp,
          states[state],
          className
        ]"
        :id="internalId"
        :name="((name && (! stringify)) ? name : null)"
        type="checkbox"
        :checked="(model === trueValue)"
        :true-value="trueValue"
        :false-value="falseValue"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        v-model="model"
        :suggested="suggested"
        :autocomplete="autocomplete"
        @click="(e: Event) => ((! disabled) ? emit('click', e) : null)"
        @change="(e: Event) => ((! disabled) ? emit('change', e) : null)"
        @blur="(e: Event) => ((! disabled) ? emit('blur', e) : null)"
        @input="(e: Event) => ((! disabled) ? emit('input', e) : null)"
        @focus="(e: Event) => ((! disabled) ? emit('focus', e) : null)" />
      <div
        v-else-if="((type == 'password') && (! hidden))"
        :class="[
          ('flex items-center relative text-auto'),
          (rounded && 'rounded-auto'),
          ((! disabled) && styles[styleName]),
          (disabled && styles['muted'])
        ]"
        :disabled="disabled">
        <input
          :ref="(el) => { refInput = el }"
          :class="[
            ('h-full pr-8 lg:pr-9 xl:pr-10 text-auto'),
            (rounded && 'rounded-auto'),
            ((! noBorder) && 'border-2 outline-none'),
            states[state],
            (block ? 'w-full' : 'w-fit'),
            ((! disabled) && styles[styleName]),
            (disabled && styles['muted']),
            ((size !== undefined) ? sizes[size] : 'padding-auto'),
            className
          ]"
          :id="internalId"
          :name="((name && (! stringify)) ? name : null)"
          :type="(pwdVisible ? 'text' : 'password')"
          :minlength="minlength"
          :maxlength="maxlength"
          :placeholder="placeholder"
          :required="required"
          :disabled="disabled"
          v-model="model"
          :suggested="suggested"
          :autocomplete="autocomplete"
          @click="(e: Event) => ((! disabled) ? emit('click', e) : null)"
          @change="(e: Event) => ((! disabled) ? emit('change', e) : null)"
          @blur="(e: Event) => ((! disabled) ? emit('blur', e) : null)"
          @input="(e: Event) => ((! disabled) ? emit('input', e) : null)"
          @focus="(e: Event) => ((! disabled) ? emit('focus', e) : null)" />
        <button
          v-if="passwordVisibility"
          @click="(e: Event) => (pwdVisible = (! pwdVisible))"
          type="button"
          class="absolute flex items-center inset-y-0 right-0 mr-3 h-full cursor-pointer text-slate-500 focus:outline-none">
          <svg
            class="size-4 lg:size-5 xl:size-6"
            width="24" height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" >
            <line v-show="(! pwdVisible)" x1="2" x2="22" y1="2" y2="22" />
            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
      </div>
      <input
        v-else-if="(! hidden)"
        :ref="(el) => { refInput = el }"
        :class="[
          ('h-full rounded-auto text-auto'),
          ((! noBorder) && 'border-2 outline-none'),
          (block ? 'w-full' : 'w-fit'),
          ((size !== undefined) ? sizes[size] : 'padding-auto'),
          ((! disabled) && styles[styleName]),
          (disabled && styles['muted']),
          states[state],
          className
        ]"
        :id="internalId"
        :name="((name && (! stringify)) ? name : null)"
        :type="type"
        :multiple="multiple"
        :min="min" :max="max" :step="step"
        :true-value="trueValue"
        :false-value="falseValue"
        :minlength="minlength"
        :maxlength="maxlength"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        v-model="model"
        :suggested="suggested"
        :autocomplete="autocomplete"
        :accept="accept"
        @click="(e: Event) => ((! disabled) ? emit('click', e) : null)"
        @change="(e: Event) => ((! disabled) ? emit('change', e) : null)"
        @blur="(e: Event) => ((! disabled) ? emit('blur', e) : null)"
        @input="(e: Event) => ((! disabled) ? emit('input', e) : null)"
        @focus="(e: Event) => ((! disabled) ? emit('focus', e) : null)" />
      <slot />
      <input
        v-if="(name && stringify)"
        :name="name"
        :value="JSON.stringify(model)"
        hidden
        :required="required" />
      <slot
        v-if="(! plain)"
        name="errors"
        :errors="errors">
        <BaseInputError
          :name="name"
          :errors="errors" />
      </slot>
    </div>
  </div>
  <input
    v-else-if="(name && stringify && hidden)"
    :id="internalId"
    :name="name"
    :value="JSON.stringify(model)"
    hidden
    :required="required" />
</template>

<style lang="scss" scoped>
input {
  &[type=checkbox] {
    @apply appearance-none;
  }
  &[type=file]::file-selector-button {
    @apply bg-black text-white duration-500 ease-in-out border-transparent hover:bg-slate-600 font-medium rounded-auto px-1 py-0.5 mr-2 sm:px-2 sm:py-1 sm:mr-4;
  }
  &[type=number]::-webkit-inner-spin-button,
  &[type=number]::-webkit-outer-spin-button {
    @apply opacity-100;
  }
}

select {
  @apply appearance-none;
}
</style>
